import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NamePipe } from 'projects/money-movement-ui/src/app/shared/pipes/NamePipe';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, skip, take, takeUntil } from 'rxjs/operators';
import { TextInputModel } from '../../../../shared/components/controls/text-input-control/TextInputModel';
import { Constants, recipientType } from '../../../../shared/utilities/Constants';
import { createControl } from '../../../utilities/formUtilities';
import { FirstPartiesOutput } from 'projects/money-movement-ui/src/app/modified-first-parties/models/FirstParties';
import { OptionDropdown, SelectControl } from '../../../models/OptionDropdownSelect';
import { selectModifiedPayeeCheckbox } from '../../../reducers';
import { Store } from '@ngrx/store';
import { selectSaModifiedPayeeCheckbox } from 'projects/money-movement-ui/src/app/standing-authorizations/reducers';
import { CheckTransactionDetailsForm } from '../../../models/form/TransactionFormGroup';

@Component({
  standalone: false,
  selector: 'mm-check-payee-name',
  templateUrl: './check-payee-name.component.html',
  styleUrls: ['./check-payee-name.component.scss'],
})
export class CheckPayeeNameComponent implements OnInit, OnChanges, OnDestroy {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm>;
  @Input() payeeType$!: Observable<string>;
  @Input() defaultPayee!: string[];
  @Input() useStandingAuth!: boolean;
  @Input() firstPartyPayees!: FirstPartiesOutput[];
  @Input() useConstructedPayee!: boolean;
  @Input() modifiedPayeeEnabled!: boolean;
  @Input() isFromEditTable!: boolean;
  @Input() shouldDisableFieldForEditQcd: boolean = false;
  payeeNameModel: TextInputModel = {
    controlName: 'payeeName',
    label: 'Payee name',
    hasGreyText: false,
    htmlMaxLength: 44
  };
  additionalPayee1Model: TextInputModel = {
    controlName: 'additionalPayeeName1',
    label: 'Payee name',
    hasGreyText: false,
    htmlMaxLength: 44
  };
  additionalPayee2Model: TextInputModel = {
    controlName: 'additionalPayeeName2',
    label: 'Payee name',
    hasGreyText: false,
    htmlMaxLength: 44
  };
  payeeNameError: string = Constants.payeeNameMissing;
  destroy$: Subject<boolean> = new Subject<boolean>();
  modifiedPayeeTooltipVerbiage = Constants.modifiedPayeeTooltipVerbiage;
  isThirdParty!: boolean;
  currentPayeeType: string = '';
  showAdditionalPayee1!: boolean;
  showAdditionalPayee2!: boolean;
  firstPartyPayeesSelectOptions: SelectControl = {
    title: 'Payee name',
    controlName: 'payeeName',
    options: [],
    validationMessage: 'Payee name is required',
    showOptionalLabel: false
  };

  constructor(private namePipe: NamePipe, private store: Store) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['firstPartyPayees'] || changes['defaultPayee']) {
      this.refreshFirstPartyOptions();
    }
  }

  ngOnInit(): void {
    createControl(this.parentForm, 'modifiedPayeeCheckbox', false);

    this.payeeType$.pipe(
      filter((payeeType) => payeeType !== ''),
      take(1),
    ).subscribe((payeeType) => {
      this.currentPayeeType = payeeType;
      this.isThirdParty = payeeType === recipientType.thirdParty;
      if (payeeType === recipientType.firstParty) {
        const payeeFullName = this.getDefaultPayeeFullName();
        createControl(this.parentForm, 'payeeName', payeeFullName, [Validators.required], undefined, undefined, this.isFromEditTable);
        if (!this.modifiedPayeeEnabled) {
          this.payeeName.disable();
        } else {
          this.constructFirstPartyPayeeOptions(payeeFullName);
        }
        this.payeeName.markAsUntouched();
        this.payeeName.markAsPristine();
        this.clearAdditionalPayees();
      } else if (this.shouldDisableFieldForEditQcd) {
        const defaultPayees = this.getDefaultPayees();
        if (defaultPayees[1]) {
          createControl(this.parentForm, 'additionalPayeeName1', defaultPayees[1], [Validators.required]);
          this.showAdditionalPayee1 = true;
          this.additionalPayeeName1.disable();
        }
        if (defaultPayees[2]) {
          createControl(this.parentForm, 'additionalPayeeName2', defaultPayees[2], [Validators.required]);
          this.showAdditionalPayee2 = true;
          this.additionalPayeeName2.disable();
        }
        this.payeeName.disable();
      } else {
        createControl(this.parentForm, 'payeeName', '', [Validators.required]);

        if (this.parentForm.get('additionalPayeeName1')) {
          createControl(this.parentForm, 'additionalPayeeName1', '', [Validators.required]);
          this.showAdditionalPayee1 = true;
        }
        if (this.parentForm.get('additionalPayeeName2')) {
          createControl(this.parentForm, 'additionalPayeeName2', '', [Validators.required]);
          this.showAdditionalPayee2 = true;
        }
      }
    });

    this.payeeType$.pipe(
      skip(1),
      takeUntil(this.destroy$),
    ).subscribe((payeeType) => {
      this.currentPayeeType = payeeType;
      this.isThirdParty = payeeType === recipientType.thirdParty;
      if (payeeType === recipientType.firstParty) {
        const payeeFullName = this.getDefaultPayeeFullName();
        this.payeeName.setValue(payeeFullName);
        if (!this.modifiedPayeeEnabled || this.shouldDisableFieldForEditQcd) {
          this.payeeName.disable();
        } else {
          this.constructFirstPartyPayeeOptions(payeeFullName);
        }
        this.payeeName.markAsUntouched();
        this.payeeName.markAsPristine();
        this.clearAdditionalPayees();
      } else {
        this.payeeName.reset();
        this.payeeName.enable();
      }
    });

    combineLatest([
      this.store.select(selectModifiedPayeeCheckbox),
      this.store.select(selectSaModifiedPayeeCheckbox)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([checked, saChecked]) => {
        if (this.modifiedPayeeEnabled) {
          if (checked || saChecked || this.shouldDisableFieldForEditQcd) {
            this.payeeName?.disable();
          } else {
            this.payeeName?.enable();
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  clearAdditionalPayees(): void {
    this.showAdditionalPayee1 = false;
    this.showAdditionalPayee2 = false;
    this.additionalPayeeName1?.reset();
    this.additionalPayeeName2?.reset();
    this.additionalPayeeName1?.clearValidators();
    this.additionalPayeeName1?.updateValueAndValidity();
    this.additionalPayeeName2?.clearValidators();
    this.additionalPayeeName2?.updateValueAndValidity();
  }

  get payeeName(): FormControl<string> {
    return this.parentForm.controls.payeeName as FormControl<string>;
  }

  get additionalPayeeName1(): FormControl<string> {
    return this.parentForm.get('additionalPayeeName1') as FormControl<string>;
  }

  get additionalPayeeName2(): FormControl<string> {
    return this.parentForm.get('additionalPayeeName2') as FormControl<string>;
  }


  addAdditionalPayee1(): void {
    createControl(this.parentForm, 'additionalPayeeName1', '', [Validators.required]);
    this.showAdditionalPayee1 = true;
  }

  addAdditionalPayee2(): void {
    createControl(this.parentForm, 'additionalPayeeName2', '', [Validators.required]);
    this.showAdditionalPayee2 = true;
  }

  removeAdditionalPayee1(): void {
    this.parentForm.removeControl('additionalPayeeName1');
    this.showAdditionalPayee1 = false;
  }

  removeAdditionalPayee2(): void {
    this.parentForm.removeControl('additionalPayeeName2');
    this.showAdditionalPayee2 = false;
  }

  getDefaultPayees(): string[] {
    return (this.defaultPayee ?? []).filter((payee) => !!payee?.trim());
  }

  getDefaultPayeeFullName(): string {
    return this.getDefaultPayees().join(this.useConstructedPayee ? ' ' : ' & ');
  }

  refreshFirstPartyOptions(): void {
    if (!this.modifiedPayeeEnabled || this.currentPayeeType !== recipientType.firstParty) {
      return;
    }

    const payeeControlValue = this.parentForm?.get('payeeName')?.value as string;
    const payeeFullName = payeeControlValue || this.getDefaultPayeeFullName();
    if (!payeeControlValue && payeeFullName) {
      this.payeeName?.setValue(payeeFullName, { emitEvent: false });
    }
    this.constructFirstPartyPayeeOptions(payeeFullName);
  }

  constructFirstPartyPayeeOptions(defaultPayee: string): void {
    const delimiter = this.useConstructedPayee ? ' ' : ' & ';
    const options = (this.firstPartyPayees ?? []).map(({ payeeNames, approvalStatus }) => {
      const payeeName = payeeNames
        .filter((name) => !!name.name.trim())
        .map((name) => name.name.trim())
        .join(delimiter);
      const payeeStatusSuffix = approvalStatus === 'Pending' ? ' (Pending)' : '';
      const payeeDisplayName = `${payeeName}${payeeStatusSuffix}`;
      return new OptionDropdown(payeeName, payeeDisplayName);
    });

    const selectedPayee = (this.parentForm?.get('payeeName')?.value as string) ?? '';
    const payeesToPreserve = [defaultPayee, selectedPayee]
      .map((payee) => (payee ?? '').trim())
      .filter((payee, index, all) => !!payee && all.indexOf(payee) === index);

    payeesToPreserve.forEach((payee) => {
      const hasOption = options.some((option) => this.normalizePayee(option.value) === this.normalizePayee(payee));
      if (!hasOption) {
        options.unshift(new OptionDropdown(payee, payee));
      }
    });

    if (!selectedPayee && payeesToPreserve[0]) {
      this.payeeName?.setValue(payeesToPreserve[0], { emitEvent: false });
    }

    this.firstPartyPayeesSelectOptions.options = options;
  }

  normalizePayee(payee: string): string {
    return (payee ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
  }
}
